import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";

import { NextUp } from "@/components/Callouts";

export default function EarTraining9() {
  const { lesson, course, accent } = findLesson("ear-training", "9");
  return (
    <LessonShell
      course={course}
      courseSlug="ear-training"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/ear-training/ET9-Rhythm-Flowchart-Animated.mp4"
        caption="The rhythmic-transcription pipeline — pulse · meter · accents · notate"
        hint="≈ 24 sec · watch first"
      />
      <Chapter number="09" eyebrow="Ear Training · Chapter Nine" title="The complete rhythm flowchart">
        <Prose>
          <p>
            One picture covers everything you need to transcribe a song's rhythm. Memorise
            this flowchart and you'll never be lost on rhythm again.
          </p>
        </Prose>

        <NoteImage
          src="/notes/ear-training/finals/03a-rhythmic-flowchart.png"
          caption="Jason's full rhythmic flowchart. Five steps, applied in order: Listen to the Song → Feel the Pulse → Identify the Time Signature & Beat Awareness → Decide Time Feel (÷2 / ÷3 / ÷4 + straight or swing) → Recognise Rhythmic Devices (the kick/snare patterns and grooves the song uses)."
        />
      </Chapter>

      <Chapter eyebrow="Walking through the chart" title="Step by step on a real song">
        <Prose>
          <p>
            Pick a song. We'll walk the chart. Let's say it's "Billie Jean" by Michael Jackson:
          </p>
        </Prose>

        <Steps>
          <Step><strong>Listen.</strong> Quincy Jones&apos;s drum machine drives the song from beat 1.</Step>
          <Step><strong>Feel the pulse.</strong> Foot taps on every drum hit — about 117 BPM.</Step>
          <Step><strong>Time signature.</strong> Count "1 2 3 4 1 2 3 4" — fits perfectly. So it's 4/4.</Step>
          <Step><strong>Time feel.</strong> Between two beats, can you fit ÷2 ('and') or ÷4 ('e and a')? The hi-hat marks every <em>eighth</em>, so it's ÷2 — count "1 and 2 and". Straight, not swung. This is the step people most often over-hear: a busy-sounding record is not automatically a sixteenth-note record.</Step>
          <Step><strong>Rhythmic devices.</strong> The kick lands on 1 and 3, snare on 2 and 4 — the standard backbeat, and the reason the groove feels so plain underneath such a famous bass line. Hi-hat on every eighth. The bass is the busy voice, not the drums: it moves in eighths and lands off the beat as often as on it, which is what makes the whole record feel restless over a completely square kit.</Step>
        </Steps>

        <Prose>
          <p>
            Five sentences, full rhythmic transcription. With the flowchart memorised you
            can do this in under thirty seconds for any song you hear.
          </p>
        </Prose>
      </Chapter>

      <NextUp
        to="/lesson/ear-training-10"
        label="Chapter 10 · A Flowchart for Melody"
        why="We close the course with the matching melody flowchart — six steps that reduce any pitched line to a complete transcription. Then you put it all together."
      />
    </LessonShell>
  );
}
